import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as UUIDv4 } from 'uuid';
import type { AxiosProgressEvent } from 'axios';
import { useTranslation } from 'react-i18next';
import { chatApi } from 'api/chat';
import { ChatImage } from 'types/Chat';
import {
	CHAT_ALLOWED_IMAGE_MIME_TYPES,
	CHAT_MAX_IMAGE_SIZE_BYTES,
	CHAT_MAX_IMAGE_SIZE_MB,
} from './chatImageUpload.constants';

export type DraftChatImageStatus = 'uploading' | 'uploaded' | 'error';

export type DraftChatImage = {
	localId: string;
	file: File;
	fileHash: string;
	originalFileName: string;
	previewUrl: string;
	status: DraftChatImageStatus;
	progress: number;
	errorMessage?: string;
	uploadedImage?: ChatImage;
};

type UseChatImageUploadsProps = {
	programMembershipId: number | null | undefined;
	interlocutorProgramMembershipId: number;
	messageUuid: string;
};

const getFileHash = async (file: File) => {
	const buffer = await file.arrayBuffer();
	const digest = await window.crypto.subtle.digest('SHA-256', buffer);
	return Array.from(new Uint8Array(digest))
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('');
};

export const useChatImageUploads = ({
	programMembershipId,
	interlocutorProgramMembershipId,
	messageUuid,
}: UseChatImageUploadsProps) => {
	const { t } = useTranslation();
	const [images, setImages] = useState<DraftChatImage[]>([]);
	const [validationMessage, setValidationMessage] = useState<string | null>(null);
	const controllerMapRef = useRef<Record<string, AbortController>>({});

	const revokePreview = useCallback((previewUrl: string) => {
		if (previewUrl.startsWith('blob:')) {
			URL.revokeObjectURL(previewUrl);
		}
	}, []);

	const removeLocalImage = useCallback(
		(localId: string) => {
			setImages((prev) => {
				const found = prev.find((item) => item.localId === localId);
				if (found) {
					revokePreview(found.previewUrl);
				}
				return prev.filter((item) => item.localId !== localId);
			});
			delete controllerMapRef.current[localId];
		},
		[revokePreview]
	);

	const uploadImage = useCallback(
		(file: File, localId: string, previewUrl: string) => {
			if (!programMembershipId) {
				setImages((prev) =>
					prev.map((item) =>
						item.localId === localId
							? {
									...item,
									status: 'error',
									progress: 0,
									errorMessage: t('chat.imageUploadUnavailable'),
								}
							: item
					)
				);
				return;
			}

			const controller = new AbortController();
			controllerMapRef.current[localId] = controller;

			const formData = new FormData();
			formData.append('file', file);
			formData.append('messageUuid', messageUuid);
			formData.append('originalFileName', file.name);

			chatApi
				.uploadChatImage(programMembershipId, interlocutorProgramMembershipId, formData, {
					signal: controller.signal,
					onUploadProgress: (event: AxiosProgressEvent) => {
						const total = event.total || file.size || 1;
						const loaded = event.loaded || 0;
						const nextProgress = Math.round((loaded / total) * 100);
						setImages((prev) =>
							prev.map((item) =>
								item.localId === localId
									? { ...item, progress: nextProgress, errorMessage: undefined }
									: item
							)
						);
					},
				})
				.then((uploadedImage) => {
					setImages((prev) =>
						prev.map((item) =>
							item.localId === localId
								? {
										...item,
										status: 'uploaded',
										progress: 100,
										errorMessage: undefined,
										uploadedImage,
										previewUrl: uploadedImage.thumbUrl,
									}
								: item
						)
					);
					revokePreview(previewUrl);
					delete controllerMapRef.current[localId];
				})
				.catch((error) => {
					if (controller.signal.aborted) {
						return;
					}
					const errorMessage =
						error?.response?.data?.message || error?.message || t('chat.imageUploadFailed');
					setImages((prev) =>
						prev.map((item) =>
							item.localId === localId ? { ...item, status: 'error', progress: 0, errorMessage } : item
						)
					);
					delete controllerMapRef.current[localId];
					// eslint-disable-next-line consistent-return
					return error;
				});
		},
		[interlocutorProgramMembershipId, messageUuid, programMembershipId, revokePreview, t]
	);

	const addFiles = useCallback(
		async (files: File[]) => {
			setValidationMessage(null);
			const validFiles = files.filter((file) => {
				if (!CHAT_ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
					return false;
				}
				if (file.size > CHAT_MAX_IMAGE_SIZE_BYTES) {
					return false;
				}
				return true;
			});

			if (validFiles.length !== files.length) {
				const hasInvalidType = files.some((file) => !CHAT_ALLOWED_IMAGE_MIME_TYPES.includes(file.type));
				const hasInvalidSize = files.some((file) => file.size > CHAT_MAX_IMAGE_SIZE_BYTES);

				if (hasInvalidType && hasInvalidSize) {
					setValidationMessage(
						t('chat.imageValidationInvalidTypeAndSize', { maxSizeMb: CHAT_MAX_IMAGE_SIZE_MB })
					);
				} else if (hasInvalidType) {
					setValidationMessage(t('chat.imageValidationInvalidType'));
				} else if (hasInvalidSize) {
					setValidationMessage(t('chat.imageValidationInvalidSize', { maxSizeMb: CHAT_MAX_IMAGE_SIZE_MB }));
				}
			}

			const existingHashes = new Set(images.map((image) => image.fileHash));
			const preparedFiles = await Promise.all(
				validFiles.map(async (file) => {
					const fileHash = await getFileHash(file);
					if (existingHashes.has(fileHash)) {
						return null;
					}

					existingHashes.add(fileHash);
					return {
						file,
						fileHash,
						localId: UUIDv4(),
						previewUrl: URL.createObjectURL(file),
					};
				})
			);

			preparedFiles.filter(Boolean).forEach((preparedFile) => {
				if (!preparedFile) {
					return;
				}
				setImages((prev) => [
					...prev,
					{
						localId: preparedFile.localId,
						file: preparedFile.file,
						fileHash: preparedFile.fileHash,
						originalFileName: preparedFile.file.name,
						previewUrl: preparedFile.previewUrl,
						status: 'uploading',
						progress: 0,
					},
				]);
				uploadImage(preparedFile.file, preparedFile.localId, preparedFile.previewUrl);
			});
		},
		[images, uploadImage]
	);

	const cancelUpload = useCallback(
		(localId: string) => {
			controllerMapRef.current[localId]?.abort();
			removeLocalImage(localId);
		},
		[removeLocalImage]
	);

	const retryUpload = useCallback(
		(localId: string) => {
			const image = images.find((item) => item.localId === localId);
			if (!image) return;
			setImages((prev) =>
				prev.map((item) =>
					item.localId === localId
						? { ...item, status: 'uploading', progress: 0, errorMessage: undefined }
						: item
				)
			);
			uploadImage(image.file, image.localId, image.previewUrl);
		},
		[images, uploadImage]
	);

	const removeImage = useCallback(
		async (localId: string) => {
			const image = images.find((item) => item.localId === localId);
			if (!image) return;

			if (image.status === 'uploading') {
				cancelUpload(localId);
				return;
			}

			if (image.uploadedImage?.id && programMembershipId) {
				await chatApi.deleteChatImage(programMembershipId, image.uploadedImage.id).catch(() => undefined);
			}

			removeLocalImage(localId);
		},
		[cancelUpload, images, programMembershipId, removeLocalImage]
	);

	const cleanupDraft = useCallback(async () => {
		Object.values(controllerMapRef.current).forEach((controller) => controller.abort());
		controllerMapRef.current = {};
		images.forEach((image) => revokePreview(image.previewUrl));
		setImages([]);
		if (programMembershipId && messageUuid) {
			await chatApi.cleanupDraftImages(programMembershipId, messageUuid).catch(() => undefined);
		}
	}, [images, messageUuid, programMembershipId, revokePreview]);

	const resetState = useCallback(() => {
		Object.values(controllerMapRef.current).forEach((controller) => controller.abort());
		controllerMapRef.current = {};
		setValidationMessage(null);
		setImages((prev) => {
			prev.forEach((image) => revokePreview(image.previewUrl));
			return [];
		});
	}, [revokePreview]);

	const restoreUploadedImages = useCallback(
		(uploadedDraftImages: ChatImage[]) => {
			Object.values(controllerMapRef.current).forEach((controller) => controller.abort());
			controllerMapRef.current = {};
			setImages((prev) => {
				prev.forEach((image) => revokePreview(image.previewUrl));
				return uploadedDraftImages.map((image) => ({
					localId: `restored-${image.id}`,
					file: null as unknown as File,
					fileHash: `restored-${image.id}`,
					originalFileName: '',
					previewUrl: image.thumbUrl,
					status: 'uploaded' as const,
					progress: 100,
					errorMessage: undefined,
					uploadedImage: image,
				}));
			});
		},
		[revokePreview]
	);

	useEffect(
		() => () => {
			Object.values(controllerMapRef.current).forEach((controller) => controller.abort());
			Object.keys(controllerMapRef.current).forEach((key) => delete controllerMapRef.current[key]);
		},
		[]
	);

	const uploadedImages = useMemo(
		() => images.map((image) => image.uploadedImage).filter(Boolean) as ChatImage[],
		[images]
	);

	const uploadedImageIds = useMemo(() => uploadedImages.map((image) => image.id), [uploadedImages]);
	const isUploading = useMemo(() => images.some((image) => image.status === 'uploading'), [images]);

	return {
		images,
		validationMessage,
		addFiles,
		cancelUpload,
		retryUpload,
		removeImage,
		cleanupDraft,
		resetState,
		restoreUploadedImages,
		uploadedImages,
		uploadedImageIds,
		isUploading,
	};
};
